/**
 * InstrumentHeader component.
 * 
 * Layout (3 columns):
 * - Left column: "Sendo" stacked over "19 Dec 2025"
 * - Center column: "White to move" stacked over "Mate in 3"  
 * - Right column: Strikes (optically centered)
 * 
 * Key alignment: Date baseline aligns with "Mate in X" baseline
 * (the bottom lines of both stacked blocks align)
 */

import { MAX_ATTEMPTS } from '@daily-puzzle/shared';
import { Link } from 'react-router-dom';
import './InstrumentHeader.css';

function formatPuzzleDate(dateStr: string): string {
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) {
        return dateStr;
    }

    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
}

export interface InstrumentHeaderProps {
    puzzleDate: string;
    attacker: 'w' | 'b';
    currentStep: number;
    totalSteps: number;
    attemptsUsed: number;
    status: 'in_progress' | 'solved' | 'missed' | 'revealed';
}

export function InstrumentHeader({
    puzzleDate,
    attacker,
    totalSteps,
    attemptsUsed,
}: InstrumentHeaderProps) {
    const colorText = attacker === 'w' ? 'White to move' : 'Black to move';
    const mateText = `Mate in ${totalSteps}`;
    const dateString = formatPuzzleDate(puzzleDate);

    return (
        <header className="daily-header">
            {/* Hidden filter definition for film grain texture */}
            <svg style={{ position: 'absolute', width: 0, height: 0, visibility: 'hidden' }}>
                <defs>
                    <filter id="header-noise">
                        <feTurbulence type="turbulence" baseFrequency="0.8" numOctaves="3" stitchTiles="stitch" result="noise" />
                        <feComponentTransfer in="noise" result="sharperNoise">
                            <feFuncR type="linear" slope="3" intercept="-1" />
                            <feFuncG type="linear" slope="3" intercept="-1" />
                            <feFuncB type="linear" slope="3" intercept="-1" />
                            <feFuncA type="linear" slope="0.5" />
                        </feComponentTransfer>
                        <feComposite operator="in" in="sharperNoise" in2="SourceGraphic" result="clippedNoise" />
                        <feBlend mode="overlay" in="clippedNoise" in2="SourceGraphic" />
                    </filter>
                </defs>
            </svg>

            {/* Left column: Sendo + Date stacked */}
            <div className="hdr-left">
                <Link to="/" className="hdr-wordmark">Sendo</Link>
                <div className="hdr-date">{dateString}</div>
            </div>

            {/* Center column: Meta (two lines stacked) */}
            <div className="hdr-center">
                <div className="hdr-metaLine">{colorText}</div>
                <div className="hdr-metaLine">{mateText}</div>
            </div>

            {/* Right column: Strikes */}
            <div className="hdr-strikes" aria-label="strikes">
                <AttemptIcons attemptsUsed={attemptsUsed} maxAttempts={MAX_ATTEMPTS} />
            </div>
        </header>
    );
}

interface AttemptIconsProps {
    attemptsUsed: number;
    maxAttempts: number;
}

function AttemptIcons({ attemptsUsed, maxAttempts }: AttemptIconsProps) {
    const icons = [];

    for (let i = 0; i < maxAttempts; i++) {
        const isUsed = i < attemptsUsed;
        icons.push(
            isUsed ? (
                <StrikeUsedIcon key={i} className="strike-icon" />
            ) : (
                <StrikeEmptyIcon key={i} className="strike-icon" />
            )
        );
    }

    return <>{icons}</>;
}

/** Filled strike icon (orange X) - used when attempt is consumed */
function StrikeUsedIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 32 32" fill="none" aria-hidden="true">
            <g filter="url(#header-noise)">
                <path
                    d="M16 8L8 0L0 8L8 16L0 24L8 32L16 24L24 32L32 24L24 16L32 8L24 0L16 8Z"
                    fill="var(--color-accent-orange)"
                />
            </g>
        </svg>
    );
}

/** Empty strike icon (outlined X) - uses currentColor for theme awareness */
function StrikeEmptyIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 32 32" fill="none" aria-hidden="true">
            <g filter="url(#header-noise)">
                <path
                    d="M31.1523 8L23.5762 15.5762L23.1514 16L23.5762 16.4238L31.1523 24L24 31.1523L16.4238 23.5762L16 23.1514L15.5762 23.5762L8 31.1523L0.847656 24L8.42383 16.4238L8.84863 16L8.42383 15.5762L0.847656 8L8 0.847656L15.5762 8.42383L16 8.84863L16.4238 8.42383L24 0.847656L31.1523 8Z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.2"
                />
            </g>
        </svg>
    );
}
